/**
 * Vue Customizer Section Events
 * Handles Shopify section events for theme editor integration
 */

(function () {
  'use strict';

  /**
   * Initialize customizer for a section
   * @param {string} sectionId
   * @param {HTMLElement} container
   */
  function initializeCustomizer(sectionId, container) {
    if (!sectionId || !container) {
      console.warn('[VueCustomizerSection] Missing sectionId or container');
      return;
    }

    // Get the data variable name for this section
    const dataVarName = `customizerData_${sectionId.replace(/-/g, '_')}`;
    const data = window[dataVarName] || window.customizerData;

    if (!data) {
      console.warn(`[VueCustomizerSection] No data found for section ${sectionId}`);
      return;
    }

    // Get section settings from the section element
    const sectionElement = container.closest(`[data-section-id="${sectionId}"]`) || container.closest('.shopify-section');

    const settings = sectionElement?.dataset?.settings ? JSON.parse(sectionElement.dataset.settings) : {};

    // Get or create manager
    const manager = window.getVueCustomizerManager(sectionId);

    // Find mount point
    const mountPoint = container.querySelector('#customizer-app');
    if (!mountPoint) {
      console.warn(`[VueCustomizerSection] No mount point found for section ${sectionId}`);
      return;
    }

    // Initialize the app
    manager.init(mountPoint, settings, data);

    // Apply initial settings
    if (Object.keys(settings).length > 0) {
      manager.applySettings(settings);
    }
  }

  /**
   * Handle section load event
   * Called when a section is first loaded or re-rendered in theme editor
   */
  function handleSectionLoad(event) {
    const section = event.target;
    const sectionId = event.detail?.sectionId || section?.dataset?.sectionId;

    if (!sectionId) return;

    // Check if this is a vue-customizer section
    const wrapper = section.querySelector('[data-skip-subtree-update]');
    if (!wrapper || !wrapper.dataset.sectionId) return;

    console.log(`[VueCustomizerSection] Section load: ${sectionId}`);

    // Short delay to ensure DOM is ready
    requestAnimationFrame(() => {
      initializeCustomizer(sectionId, wrapper);
    });
  }

  /**
   * Handle section unload event
   * Called when a section is about to be removed or re-rendered
   */
  function handleSectionUnload(event) {
    const sectionId = event.detail?.sectionId;

    if (!sectionId) return;

    // Check if we have a manager for this section
    if (window.VueCustomizerRegistry && window.VueCustomizerRegistry.has(sectionId)) {
      console.log(`[VueCustomizerSection] Section unload: ${sectionId}`);

      const manager = window.VueCustomizerRegistry.get(sectionId);
      if (manager) {
        // Save state before unload
        manager.saveState();

        // Don't destroy if we're in the editor and the container has data-skip-subtree-update
        // The DOM won't actually be replaced in this case
        const wrapper = document.querySelector(`[data-section-id="${sectionId}"][data-skip-subtree-update]`);
        if (!wrapper) {
          manager.destroy();
        }
      }
    }
  }

  /**
   * Handle section select event
   * Called when a section is selected in the theme editor
   */
  function handleSectionSelect(event) {
    const sectionId = event.detail?.sectionId;

    if (!sectionId) return;

    console.log(`[VueCustomizerSection] Section select: ${sectionId}`);

    // Ensure customizer is initialized and visible
    const wrapper = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Handle section deselect event
   */
  function handleSectionDeselect(event) {
    const sectionId = event.detail?.sectionId;

    if (!sectionId) return;

    console.log(`[VueCustomizerSection] Section deselect: ${sectionId}`);

    // Save state when section is deselected
    if (window.VueCustomizerRegistry && window.VueCustomizerRegistry.has(sectionId)) {
      const manager = window.VueCustomizerRegistry.get(sectionId);
      if (manager) {
        manager.saveState();
      }
    }
  }

  /**
   * Handle block select event
   * Called when a block within a section is selected
   */
  function handleBlockSelect(event) {
    const { sectionId, blockId } = event.detail || {};

    if (!sectionId || !blockId) return;

    console.log(`[VueCustomizerSection] Block select: ${blockId} in section ${sectionId}`);

    // Find and highlight the block
    const block = document.querySelector(`[data-block-id="${blockId}"]`);
    if (block) {
      block.scrollIntoView({ behavior: 'smooth', block: 'center' });
      block.classList.add('shopify-editor-block-selected');
    }
  }

  /**
   * Handle block deselect event
   */
  function handleBlockDeselect(event) {
    const { blockId } = event.detail || {};

    if (!blockId) return;

    // Remove highlight from block
    const block = document.querySelector(`[data-block-id="${blockId}"]`);
    if (block) {
      block.classList.remove('shopify-editor-block-selected');
    }
  }

  /**
   * Handle shopify:inspector:activate event
   * Called when theme editor inspector is activated on an element
   */
  function handleInspectorActivate(event) {
    // Save state for any active customizers
    if (window.VueCustomizerRegistry) {
      window.VueCustomizerRegistry.forEach((manager) => {
        manager.saveState();
      });
    }
  }

  /**
   * Check if we're in the Shopify theme editor
   * @returns {boolean}
   */
  function isThemeEditor() {
    return window.Shopify && window.Shopify.designMode === true;
  }

  /**
   * Initialize section event listeners
   */
  function init() {
    // Only add section event listeners if we're in the theme editor
    if (isThemeEditor()) {
      document.addEventListener('shopify:section:load', handleSectionLoad);
      document.addEventListener('shopify:section:unload', handleSectionUnload);
      document.addEventListener('shopify:section:select', handleSectionSelect);
      document.addEventListener('shopify:section:deselect', handleSectionDeselect);
      document.addEventListener('shopify:block:select', handleBlockSelect);
      document.addEventListener('shopify:block:deselect', handleBlockDeselect);
      document.addEventListener('shopify:inspector:activate', handleInspectorActivate);

      console.log('[VueCustomizerSection] Theme editor event listeners initialized');
    }

    // Initialize any customizers already on the page
    document.querySelectorAll('[data-skip-subtree-update][data-section-id]').forEach((wrapper) => {
      const sectionId = wrapper.dataset.sectionId;
      if (sectionId && !window.VueCustomizerRegistry?.has(sectionId)) {
        initializeCustomizer(sectionId, wrapper);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also handle turbolinks/instant page loads
  document.addEventListener('turbolinks:load', init);
  document.addEventListener('page:load', init);

  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    if (window.VueCustomizerRegistry) {
      window.VueCustomizerRegistry.forEach((manager) => {
        manager.saveState();
      });
    }
  });

  // Export init function for manual initialization
  window.initVueCustomizerSection = init;
  window.initializeVueCustomizer = initializeCustomizer;
})();
